import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SectionLabel, Reveal } from "../primitives";
import { Section, TCard, BODY_DARK } from "../shared";
import { modules } from "../content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../ui/accordion";
import sophiaImg from "@/assets/img/t-sophia.png";
import barbaraImg from "@/assets/img/t-barbara.png";
import luisaKundinImg from "@/assets/img/t-luisa-kundin.png";

const testimonialImgs = { sophia: sophiaImg, barbara: barbaraImg, luisa: luisaKundinImg };

export default function ModuleSection() {
  return (
    <Section testId="module-section" className="bg-espresso py-24 text-gold md:py-36">
      <SectionLabel color="gold" testId="module-label">Was Dich erwartet</SectionLabel>
      <Reveal>
        <p className={`${BODY_DARK} mb-4 max-w-3xl`}>
          In den 6 Monaten unserer Zusammenarbeit erhältst Du alles, was Du brauchst, um einen Job zu finden oder selbst zu kreieren, der sich wirklich nach Dir anfühlt. Ohne dass Du Abstriche machen musst.
        </p>
        <p className={`${BODY_DARK} mb-12 max-w-3xl text-gold`}>Darin durchläufst Du drei Schritte:</p>
      </Reveal>
      <Reveal delay={0.08}>
        <Accordion type="single" collapsible className="w-full">
          {modules.map((m, i) => (
            <AccordionItem key={m.title} value={`modul-${i}`} className="border-t border-cognac/40 border-b-0" data-testid={`modul-item-${i}`}>
              <AccordionTrigger
                data-testid={`modul-trigger-${i}`}
                className="gap-6 py-7 text-left hover:no-underline [&>svg]:text-cognac-light"
              >
                <span className="flex items-start gap-6">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-cognac-light font-heading text-xl font-700 text-cognac-light" style={{ fontWeight: 700 }}>
                    {i + 1}
                  </span>
                  <span>
                    <span className="block font-heading text-xl font-600 uppercase tracking-tight text-gold md:text-2xl" style={{ fontWeight: 600 }}>
                      {m.title}
                    </span>
                    <span className="mt-2 block font-body text-base leading-[1.7] text-gold/75 md:text-lg" style={{ fontWeight: 300 }}>
                      {m.short}
                    </span>
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-10 md:pl-[4.5rem]">
                <p className="mb-2 font-heading text-sm font-700 uppercase tracking-[0.25em] text-cognac-light" style={{ fontWeight: 700 }}>
                  Modul {i + 1}: {m.sub}
                </p>
                <p className={`${BODY_DARK} mb-8 max-w-3xl italic`}>{m.intro}</p>
                <ul className="mb-10 grid max-w-3xl gap-4 md:grid-cols-2">
                  {m.points.map(([t, d], j) => (
                    <motion.li
                      key={t}
                      initial={{ opacity: 0, y: 32, rotate: j % 2 ? 5 : -5 }}
                      animate={{ opacity: 1, y: 0, rotate: j % 2 ? 0.7 : -0.7 }}
                      transition={{ delay: 0.12 + j * 0.09, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                      style={{ transformOrigin: "bottom left" }}
                      className="flex gap-4 rounded-sm border border-cognac/50 bg-espresso-2/60 p-5 shadow-[0_14px_30px_-18px_rgba(0,0,0,0.6)]"
                    >
                      <Check className="mt-1 h-5 w-5 shrink-0 text-cognac-light" />
                      <p className="font-body text-base leading-[1.7] text-gold/85 md:text-lg">
                        <strong className="text-gold" style={{ fontWeight: 600 }}>{t}</strong> — {d}
                      </p>
                    </motion.li>
                  ))}
                </ul>
                <motion.div
                  initial={{ opacity: 0, y: 30, rotate: 2 }}
                  animate={{ opacity: 1, y: 0, rotate: 0 }}
                  transition={{ delay: 0.12 + m.points.length * 0.09, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  style={{ transformOrigin: "bottom left" }}
                  className="max-w-2xl"
                >
                  <TCard
                    dark
                    img={testimonialImgs[m.t.img]}
                    name={m.t.name}
                    lead={m.t.lead}
                    ps={m.t.ps}
                    testId={`modul-testimonial-${i}`}
                  />
                </motion.div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </Section>
  );
}
